import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate, Routes, Route} from 'react-router-dom';
import ConfirmModal from '../components/ConfirmModal';
import '../styles/Dashboard.css';
import { LuUser, LuMapPin, LuDoorClosed, LuFileChartColumnIncreasing, LuFileText, LuFileSearch, LuFileX, LuUsers, LuSun, LuMoon, LuClipboardList } from "react-icons/lu";
import RegistroInformacion from './RegistroInformacion';
import ConsultaDocumentos from './ConsultarDocumentos';
import AnulacionDocumentos from './AnulacionDocumentos';
import PrivateRoute from '../components/PrivateRoute';
import { tienePermiso, CODIGOS_PERMISOS } from '../utils/permisosHelper';
import MantenedorUsuarios from './MantenedorUsuarios';
import Graficos from './Graficos';
import Informes from './InformesDocumentos';

const RUTA_LOGO_EMPRESA = process.env.REACT_APP_RUTA_LOGO_EMPRESA || '/logo_empresa.png';
const NOMBRE_EMPRESA = process.env.REACT_APP_NOMBRE_EMPRESA || 'Mi Empresa';
const Dashboard = () => {
  const { usuario, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState('');
  const [confirmLogout, setConfirmLogout] = useState(false);

  const handleLogout = () => setConfirmLogout(true);

  const doLogout = () => {
    setConfirmLogout(false);
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleNavigate = (path, menuName) => {
    navigate(path);
    setActiveMenu(menuName);
    if (window.innerWidth < 993) {
      setSidebarOpen(false);
    }
  };

  // Funciones auxiliares para verificar permisos
  const puedeRegistrar = () => {
    return tienePermiso(usuario, CODIGOS_PERMISOS.REGISTRAR_INGRESOS) || 
           tienePermiso(usuario, CODIGOS_PERMISOS.REGISTRAR_GASTOS);
  };

  const puedeConsultar = () => {
    return tienePermiso(usuario, CODIGOS_PERMISOS.CONSULTAR_DOCUMENTOS) ||
           tienePermiso(usuario, CODIGOS_PERMISOS.CONSULTAR_DOCUMENTOS_PROPIOS) ||
           tienePermiso(usuario, CODIGOS_PERMISOS.CONSULTAR_DOCUMENTOS_GRUPO) ||
           tienePermiso(usuario, CODIGOS_PERMISOS.CONSULTAR_DOCUMENTOS_LOCAL) ||
           tienePermiso(usuario, CODIGOS_PERMISOS.CONSULTAR_TODOS_DOCUMENTOS);
  };

  const puedeAnular = () => {
    return tienePermiso(usuario, CODIGOS_PERMISOS.ANULAR_DOCUMENTOS);
  };

  const puedeVerUsuarios = () => {
    return tienePermiso(usuario, CODIGOS_PERMISOS.VER_USUARIOS);
  };

  const puedeVerInformes = () => {
    return tienePermiso(usuario, CODIGOS_PERMISOS.ACCEDER_SISTEMA);
  };

  return (
    <div className="dashboard-container">
      <button 
        className="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        ☰
      </button>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <h2 className="sidebar-title">CONTROL DE GASTOS</h2>
          <div className="sidebar-header-actions">
            <button
              className="theme-toggle-btn"
              onClick={toggleTheme}
              title={theme === 'light' ? 'Modo oscuro' : 'Modo claro'}
            >
              {theme === 'light' ? <LuMoon /> : <LuSun />}
            </button>
            <button className="sidebar-toggle" onClick={toggleSidebar}>
              {sidebarOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Perfil de usuario */}
        <div className="user-profile">
          <button className="user-avatar" onClick={() => handleNavigate('/dashboard')} >
            <span className="avatar-text">
              {usuario?.Nombre?.charAt(0)}{usuario?.ApellidoPat?.charAt(0)}
            </span>
            <span className="online-indicator"></span>
          </button>
          {sidebarOpen && (
            <div className="user-info">
              <h3 className="user-name">{usuario?.Nombre} {usuario?.ApellidoPat}</h3>
              <p className="user-role">{usuario?.rol?.nombre || usuario?.permisoInfo?.NombrePermiso}</p>
              <span className="online-badge">Online</span>
            </div>
          )}
        </div>

        {/* Menú de navegación */}
        <nav className="sidebar-nav">
          {/* Registro - Solo si puede registrar ingresos o gastos */}
          {puedeRegistrar() && (
            <button 
              className={`nav-item ${activeMenu === 'registro' ? 'active' : ''}`}
              onClick={() => handleNavigate('/dashboard/registro', 'registro')}
            >
              <i className="nav-icon"><LuFileText /></i>
              {sidebarOpen && <span>Registro de información</span>}
            </button>
          )}

          {/* Consulta - Si tiene algún permiso de consulta */}
          {puedeConsultar() && (
            <button 
              className={`nav-item ${activeMenu === 'consulta' ? 'active' : ''}`}
              onClick={() => handleNavigate('/dashboard/consulta', 'consulta')}
            >
              <i className="nav-icon"><LuFileSearch /></i>
              {sidebarOpen && <span>Consulta de documentos</span>}
            </button>
          )}

          {/* Anulación - Solo si puede anular documentos */}
          {puedeAnular() && (
            <button 
              className={`nav-item ${activeMenu === 'anulacion' ? 'active' : ''}`}
              onClick={() => handleNavigate('/dashboard/anulacion', 'anulacion')}
            >
              <i className="nav-icon"><LuFileX /></i>
              {sidebarOpen && <span>Anular documentos</span>}
            </button>
          )}

          {/* Mantenedor de Usuarios - Solo si puede ver usuarios */}
          {puedeVerUsuarios() && (
            <button 
              className={`nav-item ${activeMenu === 'usuarios' ? 'active' : ''}`}
              onClick={() => handleNavigate('/dashboard/usuarios', 'usuarios')}
            >
              <i className="nav-icon"><LuUsers /></i>
              {sidebarOpen && <span>Usuarios</span>}
            </button>
          )}
          {puedeConsultar() && (
            <button
              className={`nav-item ${activeMenu === 'graficos' ? 'active' : ''}`}
              onClick={() => handleNavigate('/dashboard/graficos', 'graficos')}
            >
              <i className="nav-icon"><LuFileChartColumnIncreasing /></i>
              {sidebarOpen && <span>Graficos</span>}
            </button>
          )}
          {puedeVerInformes() && (
            <button
              className={`nav-item ${activeMenu === 'informes' ? 'active' : ''}`}
              onClick={() => handleNavigate('/dashboard/informes', 'informes')}
            >
              <i className="nav-icon"><LuClipboardList /></i>
              {sidebarOpen && <span>Informes</span>}
            </button>
          )}
          <div className='logo-empresa'>
            <img className='logo-img'
            src={RUTA_LOGO_EMPRESA} alt={`Logo ${NOMBRE_EMPRESA}`} />
          </div>
        </nav>

        {/* Botón de cerrar sesión */}
        
        <button className="logout-btn" onClick={handleLogout}>
          <i className="nav-icon"><LuDoorClosed/></i>
          {sidebarOpen && <span>Cerrar sesión</span>}
        </button>
        
      </aside>

      <ConfirmModal
        isOpen={confirmLogout}
        title="Cerrar Sesión"
        message="¿Está seguro que desea cerrar sesión?"
        confirmLabel="Cerrar sesión"
        onConfirm={doLogout}
        onCancel={() => setConfirmLogout(false)}
      />

      {/* Contenido principal */}
      <main className={`main-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <div className="content-wrapper">
          <Routes>
            <Route path="/" element={<DashboardHome usuario={usuario} />} />
            
            {/* Rutas protegidas por permisos */}
            <Route 
              path="/registro" 
              element={
                <PrivateRoute 
                  requiredPermissions={[
                    CODIGOS_PERMISOS.REGISTRAR_INGRESOS,
                    CODIGOS_PERMISOS.REGISTRAR_GASTOS
                  ]}
                  message="No tienes permisos para registrar documentos."
                >
                  <RegistroInformacion />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/consulta" 
              element={
                <PrivateRoute 
                  requiredPermissions={[
                    CODIGOS_PERMISOS.CONSULTAR_DOCUMENTOS,
                    CODIGOS_PERMISOS.CONSULTAR_DOCUMENTOS_PROPIOS,
                    CODIGOS_PERMISOS.CONSULTAR_DOCUMENTOS_GRUPO,
                    CODIGOS_PERMISOS.CONSULTAR_DOCUMENTOS_LOCAL,
                    CODIGOS_PERMISOS.CONSULTAR_TODOS_DOCUMENTOS
                  ]}
                  message="No tienes permisos para consultar documentos."
                >
                  <ConsultaDocumentos />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/anulacion" 
              element={
                <PrivateRoute 
                  requiredPermission={CODIGOS_PERMISOS.ANULAR_DOCUMENTOS}
                  message="No tienes permisos para anular documentos."
                >
                  <AnulacionDocumentos />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/usuarios" 
              element={
                <PrivateRoute 
                  requiredPermission={CODIGOS_PERMISOS.VER_USUARIOS}
                  message="No tienes permisos para ver usuarios."
                >
                  <MantenedorUsuarios />
                </PrivateRoute>
              } 
            />
            <Route
              path="/graficos"
              element={
                <PrivateRoute
                  requiredPermission={CODIGOS_PERMISOS.ACCEDER_SISTEMA}
                  message="No tienes permisos para ver graficos."
                >
                  <Graficos />
                </PrivateRoute>
              }
            />
            <Route
              path="/informes"
              element={
                <PrivateRoute
                  requiredPermission={CODIGOS_PERMISOS.ACCEDER_SISTEMA}
                  message="No tienes permisos para ver informes."
                >
                  <Informes />
                </PrivateRoute>
              }
            />
          </Routes>
        </div>
      </main>
    </div>
  );
};

const DashboardHome = ({ usuario }) => (
  <>
    <div className="welcome-section">
      <h1>Bienvenido, {usuario?.Nombre}!</h1>
      <p className="welcome-subtitle">Sistema de Control de Gastos</p>
    </div>

    {/* Cards de información */}
    <div className="info-cards">
      <div className="info-card">
        <div className="card-icon blue">
          <span><LuUser /></span>
        </div>
        <div className="card-content">
          <h4>Usuario</h4>
          <p>{usuario?.Nombre}</p>
        </div>
      </div>

      <div className="info-card">
        <div className="card-icon green">
          <span><LuMapPin /></span>
        </div>
        <div className="card-content">
          <h4>Local</h4>
          <p>{usuario?.localInfo?.Nombre || 'No asignado'}</p>
        </div>
      </div>

      <div className="info-card">
        <div className="card-icon gray">
          <span>🔑</span>
        </div>
        <div className="card-content">
          <h4>Rol</h4>
          <p>{usuario?.rol?.nombre || usuario?.permisoInfo?.NombrePermiso}</p>
        </div>
      </div>
    </div>

    {/* Información detallada */}
    <div className="detail-section">
      <div className="detail-card">
        <h3>Información del Usuario</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">Nombre Completo:</span>
            <span className="detail-value">
              {usuario?.Nombre} {usuario?.ApellidoPat} {usuario?.ApellidoMat}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">RUT:</span>
            <span className="detail-value">{usuario?.Rut || 'No registrado'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Código Usuario:</span>
            <span className="detail-value">{usuario?.NombreUsu}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Rol:</span>
            <span className="detail-value">
              {usuario?.rol?.nombre || usuario?.permisoInfo?.NombrePermiso}
            </span>
          </div>
        </div>
      </div>

      
    </div>
  </>
);

export default Dashboard;